/**
 * Tema de Ant Design 5 para el sistema de diseño de Sistemas FCT (manual v0.9.1).
 *
 * - colorPrimary es el azul de interacción (#1C5FD6): enlaces, casillas, radios,
 *   interruptores, foco y selección. El verde es marca, nunca interacción.
 * - El botón primario se sobrescribe a lima con texto verde oscuro.
 * - Los colores de estado son los del semáforo del manual, no los de Ant Design.
 * - Radios un paso más afilados que el manual corporativo: micro 4 · control 6 · tarjeta 10 · hoja 12 (modal).
 * - fontSize es el texto de controles y tablas (14 cómoda, 13 compacta). El cuerpo de lectura
 *   (15/24) se aplica con la clase .fct-lectura.
 * - colorInfo es la línea de proceso (petróleo): lo usan Progress y Steps. Para avisos informativos
 *   usar FctAviso tipo="info" (neutro), no Alert type="info".
 *
 * Probado contra antd 5.12+. En 5.1–5.11 los tokens de componente que no existan
 * se ignoran sin error; el aspecto base se mantiene.
 */
import type { ThemeConfig } from 'antd';
import { fct } from '../tokens/tokens';

export type FctPiel = 'interna' | 'cliente';
export type FctDensidad = 'comoda' | 'compacta';

const base = {
  colorPrimary: fct.interaccion.enlace,
  colorLink: fct.interaccion.enlace,
  colorLinkHover: fct.interaccion['enlace-hover'],
  colorInfo: fct.estado.proceso.linea,
  colorSuccess: fct.estado.exito.linea,
  colorWarning: fct.estado.aviso.linea,
  colorError: fct.estado.riesgo.linea,
  colorText: fct.tinta['900'],
  colorTextSecondary: fct.tinta['700'],
  colorTextTertiary: fct.tinta['500'],
  colorTextQuaternary: fct.interaccion['deshabilitado-texto'],
  colorTextHeading: fct.tinta['900'],
  colorBorder: fct.linea.control,
  colorBorderSecondary: fct.linea.tarjeta,
  colorSplit: fct.linea.fila,
  colorBgContainer: fct.superficie.tarjeta,
  colorBgElevated: fct.superficie.tarjeta,
  colorFillAlter: fct.superficie.hundida,
  colorBgContainerDisabled: fct.interaccion['deshabilitado-fondo'],
  controlOutline: 'rgba(28, 95, 214, 0.25)',
  controlItemBgActive: fct.interaccion['seleccion-fondo'],
  controlItemBgHover: fct.interaccion['hover-fila'],
  fontFamily: fct.fuente.interfaz,
  fontFamilyCode: fct.fuente.dato,
  fontSize: 14,
  borderRadius: fct.radio.control,
  borderRadiusSM: fct.radio.micro,
  borderRadiusLG: fct.radio.tarjeta,
  borderRadiusXS: 2,
  boxShadow: fct.elev['2'],
  boxShadowSecondary: fct.elev['2'],
  wireframe: false,
} as const;

function componentes(densidad: FctDensidad): ThemeConfig['components'] {
  const control = fct.densidad[densidad].control;
  return {
    Button: {
      // Primario lima con texto verde oscuro. Uno por pantalla.
      colorPrimary: fct.interaccion.primaria,
      colorPrimaryHover: fct.interaccion['primaria-hover'],
      colorPrimaryActive: fct.interaccion['primaria-activa'],
      colorTextLightSolid: fct.interaccion['primaria-texto'],
      primaryShadow: fct.elev.boton,
      defaultBorderColor: fct.linea.control,
      defaultColor: fct.marca['verde-oscuro'],
      // Sin esto, el terciario hereda el lima en hover (texto lima sobre blanco, 1,5:1).
      defaultHoverColor: fct.marca['verde-oscuro'],
      defaultActiveColor: fct.marca['verde-oscuro'],
      defaultHoverBorderColor: fct.tinta['700'],
      defaultActiveBorderColor: fct.tinta['700'],
      defaultHoverBg: fct.interaccion['hover-fila'],
      fontWeight: 700,
      controlHeight: control,
      controlHeightSM: 32,
    },
    Input: { controlHeight: control, activeBorderColor: fct.interaccion.foco, hoverBorderColor: fct.tinta['700'] },
    InputNumber: { controlHeight: control },
    Select: { controlHeight: control, optionSelectedBg: fct.interaccion['seleccion-fondo'] },
    DatePicker: { controlHeight: control },
    Tabs: {
      // Pestaña activa: texto y subrayado verde oscuro.
      colorPrimary: fct.marca['verde-oscuro'],
      itemSelectedColor: fct.marca['verde-oscuro'],
      inkBarColor: fct.marca['verde-oscuro'],
      itemHoverColor: fct.marca['verde-oscuro'],
    },
    Segmented: { itemSelectedColor: fct.marca['verde-oscuro'], trackBg: 'rgba(255,255,255,.6)' },
    Table: {
      headerBg: fct.superficie.tarjeta,
      headerColor: fct.tinta['500'],
      headerSplitColor: 'transparent',
      borderColor: fct.linea.fila,
      rowHoverBg: fct.interaccion['hover-fila'],
      rowSelectedBg: fct.interaccion['fila-resaltada'],
      rowSelectedHoverBg: 'rgba(194, 240, 61, 0.18)',
      cellPaddingBlock: fct.densidad[densidad]['celda-v'],
      cellPaddingInline: fct.densidad[densidad]['celda-h'],
    },
    Card: { borderRadiusLG: fct.radio.tarjeta, colorBorderSecondary: fct.linea.tarjeta },
    Modal: { borderRadiusLG: fct.radio.hoja },
    Tag: { borderRadiusSM: 999 },
    Menu: {
      darkItemBg: fct.superficie.rail,
      darkSubMenuItemBg: fct.superficie.rail,
      darkItemColor: fct.rail.icono,
      darkItemSelectedBg: 'rgba(255,255,255,.08)',
      darkItemSelectedColor: fct.rail.activo,
    },
    Layout: {
      siderBg: fct.superficie.rail,
      headerBg: 'transparent',
      bodyBg: fct.superficie.lienzo,
    },
  };
}

/** Tema para una piel y una densidad. La densidad la fija el tipo de vista. */
export function fctTema(piel: FctPiel = 'interna', densidad: FctDensidad = 'comoda'): ThemeConfig {
  return {
    token: {
      ...base,
      colorBgLayout: piel === 'interna' ? fct.superficie.lienzo : fct.superficie.papel,
      controlHeight: fct.densidad[densidad].control,
      fontSize: fct.densidad[densidad]['texto-control'],
    },
    components: componentes(densidad),
  };
}

/** Atajos habituales. */
export const fctTemaInterno = fctTema('interna', 'comoda');
export const fctTemaInternoCompacto = fctTema('interna', 'compacta');
export const fctTemaCliente = fctTema('cliente', 'comoda');
